export interface PlotData {
  colHeaders: string[];
  rowLabels: string[];
  matrix: number[][];
  hasHeader: boolean;
  hasLabels: boolean;
  /** all finite numeric values, flattened (for histograms) */
  flat: number[];
}

function isNum(s: string): boolean {
  return s.trim() !== "" && Number.isFinite(Number(s));
}

function splitLine(line: string): string[] {
  // Prefer comma/tab delimiters; fall back to runs of whitespace.
  const delimited = /[,\t]/.test(line)
    ? line.split(/[,\t]/)
    : line.split(/\s+/);
  return delimited.map((c) => c.trim()).filter((c) => c !== "");
}

/**
 * Parse pasted tabular data into a numeric matrix with optional header row and
 * label column, auto-detecting both. Accepts comma-, tab- or space-separated
 * rows, and FASTA-style is not relevant here.
 */
export function parsePlotData(text: string): PlotData {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l !== "");
  const cells = lines.map(splitLine).filter((r) => r.length > 0);

  const empty: PlotData = {
    colHeaders: [],
    rowLabels: [],
    matrix: [],
    hasHeader: false,
    hasLabels: false,
    flat: [],
  };
  if (cells.length === 0) return empty;

  const allText = (row: string[]) =>
    row.length > 0 && row.every((c) => !isNum(c));
  const hasHeader = cells.length > 1 && allText(cells[0]);
  const body = hasHeader ? cells.slice(1) : cells;
  if (body.length === 0) return empty;

  const hasLabels = body[0].length > 1 && !isNum(body[0][0]);

  const colHeaders = hasHeader
    ? hasLabels
      ? cells[0].slice(1)
      : cells[0]
    : Array.from(
        {
          length: Math.max(...body.map((r) => r.length)) - (hasLabels ? 1 : 0),
        },
        (_, i) => `Series ${i + 1}`,
      );

  const rowLabels = hasLabels
    ? body.map((r) => r[0])
    : body.map((_, i) => String(i + 1));
  const matrix = body.map((r) =>
    (hasLabels ? r.slice(1) : r).map((c) => Number(c)),
  );
  const flat = matrix.flat().filter((n) => Number.isFinite(n));

  return { colHeaders, rowLabels, matrix, hasHeader, hasLabels, flat };
}
